import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";
import Header from "@/components/Header";
import { Users, Mail, Clock, ArrowRight, MapPin, Calendar } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StudiesPage() {
  const { t, language } = useLanguage();
  const [pageStudies, setPageStudies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPageStudies();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPageStudies = async () => {
    try {
      const response = await axios.get(`${API}/public/page-studies`);
      setPageStudies(response.data);
    } catch (error) {
      console.error("Error fetching studies:", error);
    } finally {
      setLoading(false);
    }
  };

  // Find Spark study from database
  const sparkStudy = pageStudies.find(s => s.name === "Spark" && s.is_active);
  
  // Other active studies (excluding Spark)
  const otherActiveStudies = pageStudies.filter(s => s.is_active && s.name !== "Spark");
  
  // Inactive studies
  const inactiveStudies = pageStudies.filter(s => !s.is_active);

  // Spark extended info (for the detailed view)
  const sparkExtendedInfo = {
    subtitle: { 
      de: "Neugier entdecken – Kinder für spannende Studie gesucht!", 
      en: "Discover Curiosity – Children wanted for exciting study!" 
    },
    fullDescription: {
      de: "Ihr Kind ist neugierig und stellt gerne Fragen? Genau das interessiert uns! In unserer Studie möchten wir verstehen, wie sich Neugier bei Kindern und Jugendlichen entwickelt. Dabei kann Ihr Kind seine natürliche Entdeckerlust einbringen, spielerische Aufgaben lösen und spannende Erfahrungen sammeln – natürlich immer gut betreut von unserem Forschungsteam.",
      en: "Is your child curious and loves asking questions? That's exactly what interests us! In our study, we want to understand how curiosity develops in children and adolescents. Your child can bring their natural desire to explore, solve playful tasks, and gather exciting experiences – always well supervised by our research team."
    },
    details: {
      de: "Ihr Kind kann an insgesamt vier Terminen teilnehmen: zwei zu Beginn und zwei ein Jahr später. Jede Sitzung dauert bis zu drei Stunden und findet in unserem kinderfreundlichen Labor an der Universität Trier statt. Die Kinder erwarten abwechslungsreiche und spielerische Aufgaben – von kleinen Quizfragen bis zu spannenden computerbasierten Aufgaben. Natürlich gibt es zwischendurch Pausen, und unser geschultes Forschungsteam sorgt jederzeit für eine angenehme und unterstützende Atmosphäre.",
      en: "Your child can participate in a total of four sessions: two at the beginning and two one year later. Each session lasts up to three hours and takes place in our child-friendly laboratory at the University of Trier. Children can expect varied and playful tasks – from small quiz questions to exciting computer-based tasks. Of course, there are breaks in between, and our trained research team ensures a pleasant and supportive atmosphere at all times."
    },
    sessions: "4",
    duration: { de: "bis 3 Stunden pro Sitzung", en: "up to 3 hours per session" },
    location: { de: "Campus I, Gebäude D, Universitätsring 15, 54296 Trier", en: "Campus I, Building D, Universitätsring 15, 54296 Trier" }
  };

  return (
    <div className="min-h-screen bg-sand-50">
      <Header />

      <main className="px-6 md:px-12 py-12 max-w-6xl mx-auto">
        <h1 className="font-outfit text-3xl sm:text-4xl font-medium text-forest-900 mb-3">
          {t("studiesPage.title")}
        </h1>
        <p className="text-lg text-stone-600 mb-10">
          {t("studiesPage.subtitle")}
        </p>

        {/* Research Focus */}
        <Card className="bg-white rounded-2xl border-stone-200 shadow-sm mb-10">
          <CardContent className="p-8">
            <h2 className="font-outfit text-xl font-medium text-forest-800 mb-4">
              {t("studiesPage.researchFocus")}
            </h2>
            <p className="text-stone-600 leading-relaxed">
              {t("studiesPage.researchDesc")}
            </p>
          </CardContent>
        </Card>

        {/* Featured Study - Spark (if active) */}
        {sparkStudy && (
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <h2 className="font-outfit text-2xl font-medium text-forest-800">
                {language === "de" ? "Aktuelle Studie" : "Current Study"}
              </h2>
              <Badge className="bg-green-100 text-green-800">{language === "de" ? "Aktiv" : "Active"}</Badge>
            </div>

            <Card className="bg-white rounded-2xl border-2 border-forest-200 shadow-md overflow-hidden">
              <div className="h-2 bg-forest-800" />
              <CardContent className="p-8">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-outfit text-2xl font-semibold text-forest-900 mb-2">
                      {sparkStudy.name}
                    </h3>
                    <p className="text-terracotta-600 font-medium">
                      {sparkExtendedInfo.subtitle[language]}
                    </p>
                  </div>
                </div>
                
                <p className="text-stone-600 leading-relaxed mb-6">
                  {sparkExtendedInfo.fullDescription[language]}
                </p>

                <div className="bg-forest-50 rounded-xl p-5 mb-6">
                  <h4 className="font-medium text-forest-900 mb-3">
                    {language === "de" ? "Was bieten wir?" : "What do we offer?"}
                  </h4>
                  <p className="text-stone-600 text-sm leading-relaxed">
                    {sparkExtendedInfo.details[language]}
                  </p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-forest-600" />
                    <div>
                      <p className="text-xs text-stone-500">{language === "de" ? "Alter" : "Age"}</p>
                      <p className="text-sm font-medium text-forest-900">{sparkStudy.age_range}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-forest-600" />
                    <div>
                      <p className="text-xs text-stone-500">{language === "de" ? "Termine" : "Sessions"}</p>
                      <p className="text-sm font-medium text-forest-900">{sparkExtendedInfo.sessions} {language === "de" ? "Sitzungen" : "sessions"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-forest-600" />
                    <div>
                      <p className="text-xs text-stone-500">{language === "de" ? "Dauer" : "Duration"}</p>
                      <p className="text-sm font-medium text-forest-900">{sparkExtendedInfo.duration[language]}</p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col md:flex-row gap-4 mb-6">
                  <div className="flex items-start gap-2 flex-1">
                    <MapPin className="h-5 w-5 text-forest-600 mt-0.5" />
                    <div>
                      <p className="text-xs text-stone-500">{language === "de" ? "Ort" : "Location"}</p>
                      <p className="text-sm text-forest-900">{sparkExtendedInfo.location[language]}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 flex-1">
                    <Mail className="h-5 w-5 text-forest-600 mt-0.5" />
                    <div>
                      <p className="text-xs text-stone-500">{language === "de" ? "Kontakt" : "Contact"}</p>
                      <a href={`mailto:${sparkStudy.contact_email}`} className="text-sm text-forest-700 hover:underline font-medium">{sparkStudy.contact_email}</a>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Link to="/buchen" className="flex-1">
                    <Button className="w-full bg-forest-800 hover:bg-forest-900 text-white rounded-full py-5">
                      {language === "de" ? "Jetzt anmelden" : "Register Now"}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                  <a href={`mailto:${sparkStudy.contact_email}`} className="flex-1">
                    <Button variant="outline" className="w-full border-forest-300 text-forest-800 hover:bg-forest-50 rounded-full py-5">
                      <Mail className="mr-2 h-4 w-4" />
                      {language === "de" ? "Kontakt aufnehmen" : "Contact Us"}
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Other Active Studies */}
        {otherActiveStudies.length > 0 && (
          <div className="mb-12">
            <h2 className="font-outfit text-xl font-medium text-forest-800 mb-6">
              {language === "de" ? "Weitere aktive Studien" : "Other Active Studies"}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {otherActiveStudies.map((study) => (
                <Card key={study.id} className="bg-white rounded-2xl border-stone-200 shadow-sm overflow-hidden">
                  <div className="h-2 bg-forest-600" />
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="font-outfit text-lg font-medium text-forest-900">{study.name}</h3>
                      <Badge className="bg-green-100 text-green-800 text-xs">{language === "de" ? "Aktiv" : "Active"}</Badge>
                    </div>
                    <p className="text-stone-600 text-sm leading-relaxed mb-4">
                      {language === "de" ? study.description_de : study.description_en}
                    </p>
                    {study.age_range && (
                      <div className="flex items-center gap-2 text-sm mb-2">
                        <Users className="h-4 w-4 text-forest-600" />
                        <span className="text-stone-600">{language === "de" ? "Alter" : "Age"}: {study.age_range}</span>
                      </div>
                    )}
                    {study.compensation && (
                      <div className="flex items-center gap-2 text-sm mb-2">
                        <Euro className="h-4 w-4 text-forest-600" />
                        <span className="text-stone-600">{study.compensation}</span>
                      </div>
                    )}
                    {study.contact_email && (
                      <div className="flex items-center gap-2 text-sm text-forest-800">
                        <Mail className="h-4 w-4" />
                        <a href={`mailto:${study.contact_email}`} className="hover:underline">{study.contact_email}</a>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* No active studies message */}
        {!sparkStudy && otherActiveStudies.length === 0 && !loading && (
          <Card className="bg-amber-50 border-amber-200 rounded-xl mb-10">
            <CardContent className="p-6 text-center">
              <p className="text-amber-800">
                {language === "de" 
                  ? "Derzeit keine aktiven Studien. Bitte schauen Sie später wieder vorbei." 
                  : "No active studies at the moment. Please check back later."}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Data Protection Notice */}
        <Card className="bg-stone-100 rounded-xl border-stone-200 mt-10">
          <CardContent className="p-5">
            <h3 className="font-medium text-stone-700 mb-2">
              {language === "de" ? "Datenschutz" : "Data Protection"}
            </h3>
            <p className="text-stone-600 text-sm">
              {language === "de" 
                ? "Alle Daten werden in pseudonymer Form und nach den gesetzlichen Bestimmungen für Datenschutz behandelt."
                : "All data is processed in pseudonymized form and in accordance with legal data protection regulations."}
            </p>
          </CardContent>
        </Card>
      </main>

      <footer className="py-6 px-6 md:px-12 border-t border-stone-200 mt-12">
        <div className="max-w-7xl mx-auto flex items-center justify-center">
          <p className="text-stone-500 text-xs">© 2026 Universität Trier. Learning Brain Lab</p>
        </div>
      </footer>
    </div>
  );
}
